/**
 * ユーザー管理関連の型定義
 */

/**
 * メール配信ステータス
 */
export type EmailDeliveryStatus =
  | 'PENDING'
  | 'SENT'
  | 'DELIVERED'
  | 'OPENED'
  | 'CLICKED'
  | 'BOUNCED'
  | 'DROPPED'
  | 'DEFERRED'
  | 'SPAM_REPORT'
  | 'UNSUBSCRIBED'
  | 'FAILED'

/**
 * ユーザーのメール配信状況
 */
export interface UserEmailDelivery {
  status: EmailDeliveryStatus | null
  deliveredAt: string | null
  userLoggedIn: boolean
  isTimedOut: boolean
  hasDeliveryIssue: boolean
  isTokenExpired: boolean
}

export type Language = 'JA' | 'EN'

export interface User {
  id: string
  email: string
  name: string
  department: string | null
  position: string | null
  role: 'SUPER_ADMIN' | 'CORPORATE_ADMIN' | 'USER'
  isActive: boolean
  preferredLanguage?: Language | null
  createdAt: string
  updatedAt: string
  _count?: {
    enrollments: number
    testResults: number
  }
  emailDelivery?: UserEmailDelivery | null
}

export interface UserDetail extends User {
  enrollments: Array<{
    id: string
    assignedAt: string
    dueDate: string | null
    completedAt: string | null
    test: {
      id: string
      title: string
      complianceRule: {
        category: string
      }
    }
  }>
  testResults: Array<{
    id: string
    score: number
    completedAt: string
    test: {
      title: string
      complianceRule: {
        category: string
      }
    }
  }>
}

export interface UserListResponse {
  users: User[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface UserFilters {
  page?: number
  limit?: number
  department?: string
  role?: string
  search?: string
  isActive?: boolean
}

export interface UserStats {
  totalUsers: number
  activeUsers: number
  roleDistribution: Array<{
    role: string
    count: number
  }>
  departmentDistribution: Array<{
    department: string
    count: number
  }>
}

export interface BulkUpdateRequest {
  userIds: string[]
  updates: {
    department?: string
    position?: string
    role?: string
    preferredLanguage?: Language
  }
}

export interface CSVImportOptions {
  updateExisting?: boolean
}

export interface CSVImportResult {
  success: boolean
  createdUsers: User[]
  updated?: User[]
  errors: Array<{
    row: number
    email?: string
    errors: string[]
  }>
  skippedUsers?: Array<{
    email: string
    name: string
    reason: string
  }>
  totalRows: number
  successCount: number
  errorCount: number
  /** メール送信がバックグラウンドで進行中かどうか */
  emailSendingInProgress?: boolean
  /** バックグラウンドで送信予定のメール件数 */
  pendingEmailCount?: number
}

export interface CSVImportResponse {
  message: string
  result: CSVImportResult
  summary?: {
    totalRows: number
    successCount: number
    errorCount: number
    createdCount: number
    updatedCount: number
  }
}

export const USER_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  CORPORATE_ADMIN: 'CORPORATE_ADMIN',
  USER: 'USER',
} as const

export const USER_ROLE_LABELS = {
  SUPER_ADMIN: 'スーパー管理者',
  CORPORATE_ADMIN: '管理者',
  USER: 'ユーザー',
} as const
